import { Explain, Hint } from '@/components/Explain';
import { STATUS_META } from '@/components/primitives';
import { cn } from '@/lib/cn';
import type { BetBundle, SourceKey, SourceStatus } from '@/types';

/**
 * Every source, always visible, with its true state.
 *
 * This is the honesty guarantee of the whole tool: support must be able to tell
 * "this bet has no logs" (empty — a real answer) from "we couldn't reach Loki"
 * (unavailable — no answer at all). Those lead to opposite conclusions, so they
 * never share a colour or a glyph.
 */

export const SOURCES: { key: SourceKey; label: string; glossary: string }[] = [
  { key: 'transactions', label: 'primary db', glossary: 'source_transactions' },
  { key: 'bet', label: 'game bet', glossary: 'source_bet' },
  { key: 'roundDetails', label: 'round', glossary: 'source_roundDetails' },
  { key: 'clickhouseRealtime', label: 'realtime', glossary: 'source_clickhouseRealtime' },
  { key: 'clickhouseMv', label: 'attempt log', glossary: 'source_clickhouseMv' },
  { key: 'clickhouseSync', label: 'settled', glossary: 'source_clickhouseSync' },
  { key: 'delayDelivery', label: 'retries', glossary: 'source_delayDelivery' },
  { key: 'sessions', label: 'sessions', glossary: 'source_sessions' },
  { key: 'freebets', label: 'freebets', glossary: 'source_freebets' },
  { key: 'rtpVariation', label: 'rtp', glossary: 'source_rtpVariation' },
  { key: 'currencyMetadata', label: 'currency', glossary: 'source_currencyMetadata' },
  { key: 'logs', label: 'logs', glossary: 'source_logs' },
  { key: 'traces', label: 'traces', glossary: 'source_traces' },
];

export function countOf(bundle: BetBundle, key: SourceKey): number {
  const data = bundle[key].data as unknown;
  if (Array.isArray(data)) return data.length;
  // The round chip counts its wagers, not "1 round".
  if (key === 'round' && data && typeof data === 'object' && 'wagers' in data) {
    return (data as { wagers: unknown[] }).wagers.length;
  }
  return data ? 1 : 0;
}

export function SourceRail({ bundle }: { bundle: BetBundle }) {
  const unavailable = SOURCES.filter((s) => bundle[s.key].status === 'unavailable');

  return (
    <section className="sticky top-[45px] z-20 border-b border-hairline bg-bg/95 backdrop-blur">
      <div className="flex items-center gap-2 px-4 py-2">
        <Explain term="reconciliation" className="eyebrow shrink-0 no-underline">
          <span className="eyebrow">sources</span>
        </Explain>

        <div className="flex flex-wrap items-center gap-1">
          {SOURCES.map((s) => (
            <Chip
              key={s.key}
              label={s.label}
              glossary={s.glossary}
              status={bundle[s.key].status}
              error={bundle[s.key].error}
              count={countOf(bundle, s.key)}
            />
          ))}
        </div>

        {unavailable.length > 0 && (
          <span className="ml-auto shrink-0 font-condensed text-[10px] font-semibold uppercase tracking-wider text-crit">
            {unavailable.length} source{unavailable.length > 1 ? 's' : ''} unreachable
          </span>
        )}
      </div>
    </section>
  );
}

function Chip({
  label,
  glossary,
  status,
  error,
  count,
}: {
  label: string;
  glossary: string;
  status: SourceStatus;
  error?: string;
  count: number;
}) {
  const meta = STATUS_META[status];

  return (
    <Hint
      side="bottom"
      content={
        <div className="space-y-1">
          <div className={cn('eyebrow', meta.color)}>{meta.label}</div>
          <div className="text-[12px]">{explainStatus(status, count)}</div>
          {error && (
            <div className="border-t border-hairline pt-1 font-mono text-[11px] text-crit">
              {error}
            </div>
          )}
        </div>
      }
    >
      <a
        href={`#section-${glossary}`}
        onClick={(e) => {
          e.preventDefault();
          document
            .getElementById(`section-${glossary}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
        className={cn(
          'inline-flex items-center gap-1 border px-1.5 py-[3px] font-mono text-[11px]',
          'border-hairline hover:border-hairline-strong',
          status === 'unavailable' && 'border-crit/40',
        )}
      >
        <span className={meta.color}>{meta.glyph}</span>
        <span className="text-text-dim">{label}</span>
        {status === 'ok' && count > 0 && (
          <span className="tnum text-text-faint">{count}</span>
        )}
      </a>
    </Hint>
  );
}

function explainStatus(status: SourceStatus, count: number): string {
  switch (status) {
    case 'ok':
      return `Returned ${count} row${count === 1 ? '' : 's'} for this bet.`;
    case 'empty':
      return 'Reached successfully — this source genuinely has nothing for this bet.';
    case 'skipped':
      return 'Not queried: either not configured in this panel, or not applicable to this bet.';
    case 'unavailable':
      return 'Could not be reached. Data may exist — we simply cannot see it right now.';
  }
}
